import { useLocation } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { historicChangesTranslations } from 'modules/bookkeepers/lib/dynamicTranslations'
import { COMPENSATION_CHANGE_IDS } from 'modules/bookkeepers/store/models/CompensationChange'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import type {
  CompensationField,
  IncidenceChangeItem,
  IncidenceChangeValue,
} from 'modules/payroll_bundle/lib/types'
import {
  compensationRecurrenceModifier,
  compensationTypeModifier,
} from 'modules/payroll_bundle/lib/utils'

import Item from './Item'

export interface Props {
  currentValue?: IncidenceChangeValue
  previousValue?: IncidenceChangeValue
  change: IncidenceChangeItem
}

const compensationFields: CompensationField[] = [
  {
    field: 'taxonomy_name',
    translation_key: 'title',
  },
  {
    field: 'description',
    translation_key: 'description',
  },
  {
    field: 'recurrence',
    translation_key: 'recurrence',
    value_modifier: compensationRecurrenceModifier,
  },
  {
    field: 'type',
    translation_key: 'type',
    value_modifier: compensationTypeModifier,
  },
]

const IncidenceCompensationChangesItem = ({ previousValue, currentValue, change }: Props) => {
  const company = companies.current
  const isDeleted = change.id === COMPENSATION_CHANGE_IDS.compensation_deleted
  const { navigation } = useNavigation()
  const incidence = useCurrentIncidence()
  const { pathname } = useLocation()
  const { hasCompensations } = useHasCompensations()
  const updatesPath = hasCompensations
    ? navigation.compensations.employeeUpdates.idChanges.fullPath({
        id: incidence.id,
      })
    : navigation.payrollBundle.incidences.idChanges.fullPath({
        id: incidence.id,
      })
  const informationPath = hasCompensations
    ? navigation.compensations.employeeUpdates.id.fullPath({ id: incidence.id })
    : navigation.payrollBundle.incidences.id.fullPath({
        id: incidence.id,
      })
  const author = change.changedBy

  return (
    <>
      {pathname === updatesPath && (
        <Text.Normal size="200" weight="semibold" color="grey1000">
          {historicChangesTranslations(change.id)}
        </Text.Normal>
      )}
      {pathname === informationPath && (
        <Text.Normal size="200" weight="semibold" color="grey800">
          {`${historicChangesTranslations(change.id)}:`}
        </Text.Normal>
      )}
      {currentValue && (
        <Box background="grey200" padding="s16" borderRadius={{ all: 'abs008' }} gap="s4">
          <Text.Normal size="200" weight="semibold" overflowWrap color="grey1000">
            {currentValue.taxonomy_name}
          </Text.Normal>
          {compensationFields.map((field) => (
            <Item
              key={field.field}
              compensationField={field}
              previousValue={previousValue}
              currentValue={currentValue}
              company={company}
              legalEntityId={incidence.legalEntity?.id}
              deleted={isDeleted}
            />
          ))}
        </Box>
      )}
      {pathname === updatesPath && author && (
        <Text.Normal size="100" weight="normal">
          {i18n.t('bookkeepers.incidence.incidence_status_text.by_text', { author })}
        </Text.Normal>
      )}
    </>
  )
}

export default IncidenceCompensationChangesItem
